import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { SelectOption } from "features/ui/Select";

import { CustomDropdown } from "./components/CustomDropdown";
import { CustomInput } from "./components/CustomInput";
import { CustomOption } from "./components/CustomOption";
import { CustomSelectedValue } from "./components/CustomSelectedValue";
import { FilterSkeleton } from "./components/FilterSkeleton";
import { DEFAULT_WIDTH_PX, SELECTED_TAGS_TO_SHOW } from "./constants";
import { useMultipleSelectFilter } from "./hooks";
import { SelectFilterProps } from "./types";

const MultipleSelectFilter = (props: SelectFilterProps) => {
  const { t } = useTranslation("filters");
  const {
    fullWidth = true,
    placeholder = t("placeholders.select_values"),
    disabled,
    isInsideFilterBuilder,
    autoFocus,
    testId,
    fieldName,
    limitTags: limitTagsProp,
  } = props;

  const {
    input,
    setInput,
    selectedValues,
    open,
    loading,
    options,
    tempSelected,
    initialized,
    onTempSelected,
    noResultsMessage,
    onChangeHandler,
    handleOpen,
    handleClose,
    handleToggle,
    filterOptions,
    isMaxValueLimitReached,
    handleCustomOptionChange,
    handleSelectAll,
    handleClear,
  } = useMultipleSelectFilter(props);

  const customDropDown = (paperProps: any) => (
    <CustomDropdown
      {...paperProps}
      searchTerm={input}
      setSearchTerm={setInput}
      onSelectAll={handleSelectAll}
      onClear={handleClear}
    />
  );

  const customOption = (props: any, option: SelectOption) => (
    <CustomOption
      {...props}
      option={option}
      tempSelected={tempSelected}
      setTempSelected={onTempSelected}
      isMaxValueLimitReached={isMaxValueLimitReached}
    />
  );

  const customInput = (params: any) => (
    <CustomInput
      {...params}
      selectedValues={selectedValues}
      placeholder={placeholder}
    />
  );

  const customSelectedValue = (value: SelectOption[], getTagProps: any) => (
    <CustomSelectedValue
      maxShownValues={limitTagsProp ?? SELECTED_TAGS_TO_SHOW}
      values={value}
      setValues={handleCustomOptionChange}
      fieldName={fieldName}
      getTagProps={getTagProps}
      onClick={handleToggle}
    />
  );

  if (!initialized) {
    return <FilterSkeleton />;
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Autocomplete
        multiple
        id={`${fieldName}-autocomplete`}
        data-testid={testId || `${fieldName}-autocomplete`}
        // Use Custom Dropdown Paper
        slots={{
          paper: customDropDown,
        }}
        forcePopupIcon
        autoFocus={autoFocus}
        disableCloseOnSelect
        disabled={disabled}
        options={options}
        value={selectedValues}
        open={open}
        onOpen={handleOpen}
        // Toggle shut when the trigger/arrow is clicked again, or on Escape.
        // "blur" is intentionally ignored so clicks inside the custom dropdown
        // paper don't close it — outside clicks go through the ClickAwayListener.
        onClose={(_, reason) => {
          if (reason === "toggleInput" || reason === "escape") {
            handleClose();
          }
        }}
        disableClearable
        slotProps={{
          popper: {
            className: isInsideFilterBuilder ? "filterBuilder" : "",
            placement: "bottom-start",
          },
          paper: {
            sx: {
              minWidth: "22rem",
            },
          },
        }}
        filterOptions={filterOptions}
        loading={loading}
        getOptionLabel={(value) =>
          typeof value === "string"
            ? value
            : (value as SelectOption).value.toString()
        }
        getOptionKey={(option) => option.id}
        noOptionsText={noResultsMessage}
        sx={fullWidth ? undefined : { width: DEFAULT_WIDTH_PX }}
        fullWidth={fullWidth}
        onChange={onChangeHandler}
        renderValue={customSelectedValue}
        renderOption={customOption}
        renderInput={customInput}
      />
    </ClickAwayListener>
  );
};

export default MultipleSelectFilter;
